import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { GridColDef, GridRenderCellParams, GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from '@mui/x-data-grid-premium';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import TimelineIcon from '@mui/icons-material/Timeline';
import { FormField } from 'src/core/forms/FormField';
import CustomButton from 'src/core/ui/CustomButton';
import DetailPanelContainer from 'src/core/tables/helpers/DetailPanelContainer';
import { CustomDataGrid } from 'src/core/tables/CustomDataGrid';
import tableHelper from 'src/core/tables/tableHelper';
import { tableSettingsProps, useTableSettings } from 'src/core/tables/useTableSettings';
import { useSettingsContext } from 'src/core/settings/useSettings';
import { useMessageContext } from 'src/shared/support/MessageContext';
import { useProductPurchaseHistory } from '../purchasingRequests';
import { useGetProductDispatchRate } from 'src/pages/warehouses/warehouseRequests';

export const PurchaseOrderRowEditor = ({ editor, rows, rowIdField, onApply, requestUrl, disabled = false, canEdit = () => true, getReadOnlyReason, limitPriceToList = false, label = 'Edit purchase order row' }: PurchaseOrderRowEditorProps) => {
  const { rowDraft, setRowDraft, applying: applyingRow, setApplying, busyRef, focusRef, visible } = editor;
  const rowFormRef = useRef<HTMLDivElement>(null);
  const compactRowActions = useMediaQuery('(max-width: 700px)');
  const { updateMessageStatus } = useMessageContext();
  useEffect(() => {
    if (!visible || !rowDraft || !focusRef.current) return;
    focusRef.current = false;
    rowFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const input = rowFormRef.current?.querySelector<HTMLInputElement>('input[name="Quantity"]');
    input?.focus({ preventScroll: true });
    input?.select();
  }, [visible, rowDraft, focusRef]);
  useEffect(() => {
    if (rowDraft && !rows.some(row => String(row[rowIdField]) === String(rowDraft.row[rowIdField]))) setRowDraft(null);
  }, [rows, rowDraft, rowIdField, setRowDraft]);
  const rowQuantity = Number(rowDraft?.Quantity);
  const rowPrice = Number(rowDraft?.NegotiatedPrice);
  const rowListPrice = Number(rowDraft?.row.ListPriceOrderCurrency ?? rowDraft?.row.ListPrice ?? rowDraft?.row.ListPriceDefaultCurrency) || 0;
  const rowQuantityError = !!rowDraft && canEdit(rowDraft.row, 'Quantity') && (!rowDraft.Quantity.trim() || !Number.isFinite(rowQuantity) || rowQuantity <= 0);
  const rowPriceError = !!rowDraft && canEdit(rowDraft.row, 'NegotiatedPrice') && (!rowDraft.NegotiatedPrice.trim() || !Number.isFinite(rowPrice) || rowPrice < 0 || (limitPriceToList && rowListPrice > 0 && rowPrice > rowListPrice));
  const readOnlyReason = rowDraft ? getReadOnlyReason?.(rowDraft.row) : undefined;
  const rowFormProps = { values: { ArticleNumber: rowDraft?.row.Product?.Key ?? rowDraft?.row.Product_Key ?? '', Product: rowDraft?.row.Product?.Value ?? rowDraft?.row.Product_Value ?? '', ListPrice: rowDraft ? rowListPrice : '', Quantity: rowDraft?.Quantity ?? '', NegotiatedPrice: rowDraft?.NegotiatedPrice ?? '', Discount: rowDraft && !rowPriceError && rowListPrice > 0 ? Math.round((1 - rowPrice / rowListPrice) * 10000) / 100 : '' }, errors: { Quantity: rowQuantityError ? 'Enter a quantity greater than 0.' : undefined, NegotiatedPrice: rowPriceError ? limitPriceToList ? 'Enter a price between 0 and the list price.' : 'Enter a nonnegative price.' : undefined }, touched: { Quantity: !!rowDraft, NegotiatedPrice: !!rowDraft }, handleChange: (event) => { const { name, value } = event.target; setRowDraft(current => current ? { ...current, [name]: value } : null); } };
  const applyRowDraft = async () => {
    if (!rowDraft || rowQuantityError || rowPriceError || disabled || busyRef.current || !canEdit(rowDraft.row, 'Quantity') || !canEdit(rowDraft.row, 'NegotiatedPrice')) return;
    busyRef.current = true;
    setApplying(true);
    try {
      const updated = await onApply(rowDraft.row[rowIdField], { Quantity: rowQuantity, NegotiatedPrice: rowPrice });
      setRowDraft({ row: updated, Quantity: String(updated.Quantity), NegotiatedPrice: String(updated.NegotiatedPrice) });
    } catch (error) {
      updateMessageStatus({ requestUrl, status: 'error', message: error instanceof Error ? error.message : 'Could not update the row.' });
    } finally {
      busyRef.current = false;
      setApplying(false);
    }
  };
  if (!visible) return null;
  return (
    <Box ref={rowFormRef} role="region" aria-label={label} sx={{ width: 1, boxSizing: 'border-box', px: { xs: 1.5, md: 2.5 }, py: 1.75, my: 1, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider', containerType: 'inline-size', overflowX: 'auto' }} onKeyDown={(event) => { if (event.key === 'Enter' && event.target instanceof HTMLInputElement && !event.nativeEvent.isComposing) { event.preventDefault(); void applyRowDraft(); } }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gridTemplateAreas: '"article article product product product list" "quantity price price discount actions actions"', gap: 1, alignItems: 'start', minWidth: 540, '& > .MuiGrid-item': { maxWidth: 'none', flexBasis: 'auto', minWidth: 0 }, '& > :nth-child(1)': { gridArea: 'article' }, '& > :nth-child(2)': { gridArea: 'product' }, '& > :nth-child(3)': { gridArea: 'list' }, '& > :nth-child(4)': { gridArea: 'quantity' }, '& > :nth-child(5)': { gridArea: 'price' }, '& > :nth-child(6)': { gridArea: 'discount' }, '& .MuiFormControl-root': { my: 0 }, '& .MuiButton-root': { mt: 0, whiteSpace: 'nowrap', height: 40 }, '@container (min-width: 820px)': { gridTemplateColumns: 'minmax(80px, 1fr) minmax(140px, 1.8fr) minmax(70px, 0.7fr) minmax(75px, 0.8fr) minmax(110px, 1.1fr) minmax(80px, 0.8fr) max-content', gridTemplateAreas: '"article product list quantity price discount actions"' } }}>
        <FormField fieldProps={{ name: 'ArticleNumber', label: 'Article number', md: 2, xs: 12, disabled: true }} formikProps={rowFormProps} />
        <FormField fieldProps={{ name: 'Product', label: 'Product', md: 3, xs: 12, disabled: true }} formikProps={rowFormProps} />
        <FormField fieldProps={{ name: 'ListPrice', label: 'List price', md: 1, xs: 12, disabled: true }} formikProps={rowFormProps} />
        <FormField fieldProps={{ name: 'Quantity', label: 'Quantity', md: 2, xs: 12, disabled: !rowDraft || applyingRow || disabled || !canEdit(rowDraft?.row, 'Quantity') }} formikProps={rowFormProps} />
        <FormField fieldProps={{ name: 'NegotiatedPrice', label: 'Negotiated price', md: 2, xs: 12, disabled: !rowDraft || applyingRow || disabled || !canEdit(rowDraft?.row, 'NegotiatedPrice') }} formikProps={rowFormProps} />
        <FormField fieldProps={{ name: 'Discount', label: 'Discount (%)', md: 2, xs: 12, disabled: true }} formikProps={rowFormProps} />
        <Box sx={{ gridArea: 'actions', display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end', '& .MuiIconButton-root': { width: 30, height: 40 } }}>
          <CustomButton iconButton size="small" title="Hide row form" tooltipText="Hide the row editor. Use a row’s up-arrow button to open it again." icon={<VisibilityOffIcon />} disabled={applyingRow || disabled} onClick={() => editor.hide()} />
          <CustomButton iconButton size="small" title="Clear row form" tooltipText="Clear the row editor without deleting the row." icon={<ClearIcon />} disabled={!rowDraft || applyingRow || disabled} onClick={() => setRowDraft(null)} />
          <Box sx={{ width: compactRowActions ? 30 : 112, flexShrink: 0 }}>
            <CustomButton iconButton={compactRowActions} size="small" title="Apply row" tooltipText={readOnlyReason ?? 'Apply these values to the order row. Save the order to persist them.'} icon={<ArrowDownwardIcon />} isSubmitting={applyingRow} disabled={ !rowDraft || rowQuantityError || rowPriceError || disabled || !canEdit(rowDraft?.row, 'Quantity') || !canEdit(rowDraft?.row, 'NegotiatedPrice') } onClick={() => void applyRowDraft()} />
          </Box>
        </Box>
      </Box>
      {readOnlyReason && <Typography role="status" variant="body2" color="text.secondary" sx={{ mt: 1 }}>{readOnlyReason}</Typography>}
    </Box>
  );
};

export const usePurchaseOrderRowEditor = ({ preferenceKey, identity, initialRows }: { preferenceKey: string; identity: any; initialRows: any[] }) => {
  const { settings, setSetting } = useSettingsContext();
  const visible = settings?.[preferenceKey] ?? true;
  const [rowDraft, setRowDraft] = useState<{ row: any; Quantity: string; NegotiatedPrice: string } | null>(null);
  const [applying, setApplying] = useState(false);
  const busyRef = useRef(false);
  const focusRef = useRef(false);
  useEffect(() => { setRowDraft(null); }, [identity, initialRows]);
  const showRow = (row: any, fallbackPrice?: number) => {
    if (busyRef.current) return;
    focusRef.current = true;
    if (!visible) setSetting(preferenceKey, true);
    const price = Number(row.NegotiatedPrice ?? row.ItemPriceOrderCurrency ?? fallbackPrice ?? row.ListPriceOrderCurrency ?? row.ListPrice ?? row.ListPriceDefaultCurrency ?? 0);
    setRowDraft({ row, Quantity: String(row.Quantity ?? ''), NegotiatedPrice: String(Number.isFinite(price) ? price : 0) });
  };
  return { visible, rowDraft, setRowDraft, applying, setApplying, busyRef, focusRef, showRow, hide: () => setSetting(preferenceKey, false) };
};

type PurchaseOrderRowEditorProps = { editor: ReturnType<typeof usePurchaseOrderRowEditor>; rows: any[]; rowIdField: string; onApply: (id: string | number, patch: { Quantity: number; NegotiatedPrice: number }) => Promise<any>; requestUrl: string; disabled?: boolean; canEdit?: (row: any, field: 'Quantity' | 'NegotiatedPrice') => boolean; getReadOnlyReason?: (row: any) => string | undefined; limitPriceToList?: boolean; label?: string };

export const PurchaseOrderRowEditButton = ({ row, onEditRow, getReadOnlyReason }: { row: any; onEditRow: (row: any) => void; getReadOnlyReason?: (row: any) => string | undefined }) => {
  const reason = getReadOnlyReason?.(row);
  return <CustomButton iconButton size="small" title="Edit row in form" tooltipText={reason ?? 'Load this row into the editor above the table.'} disabled={Boolean(reason)} icon={<ArrowUpwardIcon />} onClick={(event) => { event.stopPropagation(); if (!reason) onEditRow(row); }} />;
};

// Grid toggles need aria-expanded, roving tabIndex and key handling that CustomButton does not forward.
export const PurchaseOrderRowHistoryToggle = ({ params }: { params: GridRenderCellParams }) => (
  <Tooltip title={params.value ? 'Hide product insights' : 'Show product insights'}>
    <IconButton size="small" aria-label={params.value ? 'Hide product insights' : 'Show product insights'} aria-expanded={Boolean(params.value)} tabIndex={params.hasFocus ? 0 : -1} color={params.value ? 'primary' : 'default'} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') event.stopPropagation(); }} onClick={(event) => { event.stopPropagation(); (params.api as unknown as { toggleDetailPanel: (id: string | number) => void }).toggleDetailPanel(params.id); }} sx={{ bgcolor: params.value ? 'action.selected' : undefined }}>
      <TimelineIcon fontSize="small" />
    </IconButton>
  </Tooltip>
);

export const purchaseOrderRowHistoryColumn: GridColDef = { ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF, headerName: '', description: 'Purchase history and dispatch rate', width: 48, hideable: false, disableExport: true, renderCell: params => <PurchaseOrderRowHistoryToggle params={params} /> };
export const createPurchaseOrderRowEditColumn = (onEditRow: (row: any) => void, getReadOnlyReason?: (row: any) => string | undefined): GridColDef => ({ field: 'editInForm', headerName: '', width: 56, sortable: false, filterable: false, disableColumnMenu: true, disableExport: true, renderCell: params => <PurchaseOrderRowEditButton row={params.row} onEditRow={onEditRow} getReadOnlyReason={getReadOnlyReason} /> });

export const PurchaseOrderRowDetailPanel = ({ row }: { row: any }) => {
  const r = row?.row ?? row ?? {};
  const productId = r?.Product?.Key ?? r?.Product_Key ?? r?.ProductId ?? r?.ArticleNumber;
  const hasProductId = (typeof productId === 'string' && productId.trim() !== '') || typeof productId === 'number';
  return (
    <DetailPanelContainer>
      <Box sx={{ width: 1, minWidth: 0, p: 1 }}>
        {hasProductId ? (
          <ProductHistoryCharts key={String(productId)} productId={productId} />
        ) : (
          <Typography color="text.secondary">Select a product to view its history.</Typography>
        )}
      </Box>
    </DetailPanelContainer>
  );
};

export const useProductPurchaseHistoryTable = (productId: string | number | undefined): readonly [GridColDef[], tableSettingsProps] => {
  const rows = useProductPurchaseHistory(productId);
  const idPath = ['PurchaseOrderId'];
  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'PurchaseOrderId', headerName: 'PO Id', width: 100, hideable: false },
      { field: 'ProductId', headerName: 'Article Number', width: 140 },
      { field: 'ProductName', headerName: 'Product', flex: 1, minWidth: 220 },
      { field: 'SupplierName', headerName: 'Supplier', width: 200 },
      { field: 'ConvertedPriceCurrencyVerbose', headerName: 'Price', width: 120 },
      { field: 'Quantity', headerName: 'Qty Ordered', type: 'number', width: 120 },
      { field: 'ReceivedQty', headerName: 'Qty Received', type: 'number', width: 130 },
      { field: 'CreatedDate', headerName: 'Created', type: 'dateTime', width: 170, valueGetter: tableHelper.getDateColumn },
      { field: 'ReceivedDate', headerName: 'Received', type: 'dateTime', width: 170, valueGetter: tableHelper.getDateColumn },
      // Numeric status code provided in sample (e.g., 34)
      { field: 'PurchaseOrderStatus', headerName: 'PO Status Code', width: 140 }
    ],
    []
  );
  const tableSettings = useTableSettings({ columns, idPath, rows, tableName: 'ProductPurchaseHistoryTable' + (productId ?? '') });
  tableSettings.hasToolbar = false;
  tableSettings.autoHeight = true;
  tableSettings.loading = false;
  tableSettings.hideFooter = true;
  tableSettings.density = 80;
  return [columns, tableSettings] as const;
};

export const ProductPurchaseHistoryTable = ({ productId }: { productId: string | number }) => {
  const [columns, tableSettings] = useProductPurchaseHistoryTable(productId);
  return <CustomDataGrid columns={columns} tableSettings={tableSettings} />;
};

type HistoryPoint = { label: string; date: number | null; values: Array<number | null>; detail: string; extra?: string };
type HistorySeries = { label: string; color: string };
const chartNumber = (value: unknown): number | null => {
  if (value == null || typeof value === 'boolean' || String(value).trim() === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};
const formatChartNumber = (value: unknown) => {
  const number = chartNumber(value);
  return number === null ? '—' : number.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
// Use calendar dates from the API so midnight values do not move a day in another timezone.
const chartDate = (value: unknown): number | null => {
  const day = String(value ?? '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
  const timestamp = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === day ? timestamp : null;
};
const formatChartDate = (date: number | null, full = false) => date === null ? 'Undated' : new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', ...(full ? { year: 'numeric' as const } : {}), timeZone: 'UTC' });
// Supports a direct payload and the usual useResponse/Axios wrappers without parsing display strings.
const unwrapHistoryResponse = (response: any): any => {
  let value = response;
  for (let depth = 0; depth < 5 && value && !Array.isArray(value); depth++) {
    if (value.ProductDispatchRateData || value.ItemsInStock) return value;
    const next = value.data ?? value.response ?? value.result ?? value.rows;
    if (next === undefined || next === value) break;
    value = next;
  }
  return value;
};
const getDispatchData = (response: any, productId: string | number): any => {
  const raw = unwrapHistoryResponse(response);
  const matchesProduct = (item: any) => String(item?.ArticleNumber ?? item?.ProductId ?? item?.ProductDispatchRateData?.ProductId ?? '') === String(productId);
  const root = Array.isArray(raw) ? raw.find(matchesProduct) ?? (raw.length === 1 ? raw[0] : null) : raw;
  const data = root?.ProductDispatchRateData ?? (Array.isArray(root?.ItemsInStock) ? root : null);
  const returnedId = data?.ProductId ?? root?.ArticleNumber;
  return returnedId != null && String(returnedId) !== String(productId) ? null : data;
};
const getPurchaseHistoryPoints = (response: any): HistoryPoint[] => {
  const rows = unwrapHistoryResponse(response);
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row) => {
      const date = chartDate(row.CreatedDate);
      const shortDate = date === null ? `PO ${row.PurchaseOrderId ?? '—'}` : new Date(date).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit', timeZone: 'UTC' });
      return { label: shortDate, date, values: [chartNumber(row.Quantity), chartNumber(row.ReceivedQty)], detail: `PO ${row.PurchaseOrderId ?? '—'} · ${formatChartDate(date, true)}`, extra: row.ConvertedPriceCurrencyVerbose ? `Unit price: ${row.ConvertedPriceCurrencyVerbose}` : undefined };
    })
    .sort((a, b) => (a.date ?? Infinity) - (b.date ?? Infinity));
};
const getStockHistoryPoints = (data: any): HistoryPoint[] => {
  const days = new Map<number, Map<string, number>>();
  for (const item of Array.isArray(data?.ItemsInStock) ? data.ItemsInStock : []) {
    const date = chartDate(item.ClosingDate);
    const balance = chartNumber(item.ClosingBalance);
    if (date === null || balance === null) continue;
    if (!days.has(date)) days.set(date, new Map());
    // Sum storage items per day; a duplicate record for the same item/day must not double stock.
    days.get(date)!.set(String(item.StorageItemId ?? 'default'), balance);
  }
  return Array.from(days, ([date, balances]) => ({ label: formatChartDate(date), date, values: [Array.from(balances.values()).reduce((sum, balance) => sum + balance, 0)], detail: `${formatChartDate(date, true)} · Closing stock` })).sort((a, b) => a.date! - b.date!);
};

// The API sample exposes unit price as a localized amount + ISO currency (e.g. "600,00 SEK").
// Keep currencies separate and reject ambiguous prices instead of showing a misleading total.
const readHistoryUnitPrice = (value: unknown): { amount: number; currency: string } | null => {
  const text = String(value ?? '').trim();
  const currency = text.match(/\b[A-Z]{3}\b/g);
  if (currency?.length !== 1) return null;
  let number = text.replace(currency[0], '').replace(/[\s\u00a0\u202f]/g, '');
  if (/^[+-]?\d+(?:[.,]\d{1,2})?$/.test(number)) number = number.replace(',', '.');
  else if (/^[+-]?\d{1,3}(?:,\d{3})+\.\d{1,2}$/.test(number)) number = number.replace(/,/g, '');
  else if (/^[+-]?\d{1,3}(?:\.\d{3})+,\d{1,2}$/.test(number)) number = number.replace(/\./g, '').replace(',', '.');
  else return null;
  const amount = chartNumber(number);
  return amount === null ? null : { amount, currency: currency[0] };
};
const getPurchaseHistoryTotals = (response: any) => {
  const raw = unwrapHistoryResponse(response);
  const rows: any[] = Array.isArray(raw) ? raw : [];
  const money = new Map<string, number>();
  let ordered = 0, received = 0, quantityComplete = true, receivedComplete = true, moneyComplete = true;
  rows.forEach((row) => {
    const quantity = chartNumber(row.Quantity), receivedQty = chartNumber(row.ReceivedQty), price = readHistoryUnitPrice(row.ConvertedPriceCurrencyVerbose);
    if (quantity === null) quantityComplete = false;
    else ordered += quantity;
    if (receivedQty === null) receivedComplete = false;
    else received += receivedQty;
    if (quantity === null || price === null) moneyComplete = false;
    else money.set(price.currency, (money.get(price.currency) ?? 0) + quantity * price.amount);
  });
  return { ordered: quantityComplete && rows.length ? ordered : null, received: receivedComplete && rows.length ? received : null, money: moneyComplete ? Array.from(money, ([currency, amount]) => `${formatChartNumber(Math.round(amount * 100) / 100)} ${currency}`) : [] };
};
const isDispatchReferenceResponse = (response: any) => {
  const data = unwrapHistoryResponse(response);
  return Array.isArray(data?.OrderTypes) || Array.isArray(data?.DeliveryMethods);
};

// One compact chart surface. Details appear on hover/focus, never in a permanent footer.
const ProductHistoryChart = ({ points, series, kind, label }: { points: HistoryPoint[]; series: HistorySeries[]; kind: 'bar' | 'line'; label: string }) => {
  const theme = useTheme();
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const measure = () => setWidth(Math.max(240, host.clientWidth));
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);
  useEffect(() => { setActiveIndex(null); }, [points]);
  const height = 190, left = 46, right = 26, top = 26, bottom = 30;
  const plotWidth = width - left - right, plotHeight = height - top - bottom;
  let minimum = 0, maximum = 0;
  points.forEach((point) => point.values.forEach((value) => {
    if (value !== null) {
      minimum = Math.min(minimum, value);
      maximum = Math.max(maximum, value);
    }
  }));
  const roughStep = (maximum - minimum || 1) / 4;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const tickStep = ([1, 2, 2.5, 5, 10].find((step) => step >= roughStep / magnitude) ?? 10) * magnitude;
  const lower = Math.floor(minimum / tickStep) * tickStep, upper = Math.max(lower + tickStep, Math.ceil(maximum / tickStep) * tickStep);
  const ticks = Array.from({ length: Math.round((upper - lower) / tickStep) + 1 }, (_, i) => lower + i * tickStep);
  const y = (value: number) => top + ((upper - value) / (upper - lower)) * plotHeight;
  const firstDate = points[0]?.date, lastDate = points[points.length - 1]?.date;
  const x = (index: number) => kind === 'line' && firstDate != null && lastDate != null && lastDate > firstDate ? left + (((points[index].date ?? firstDate) - firstDate) / (lastDate - firstDate)) * plotWidth : left + ((index + 0.5) / Math.max(1, points.length)) * plotWidth;
  const labelCount = Math.min(points.length, Math.max(2, Math.floor(plotWidth / 70)));
  const labelIndices = new Set(Array.from({ length: labelCount }, (_, i) => (labelCount === 1 ? 0 : Math.round((i * (points.length - 1)) / (labelCount - 1)))));
  const selectedIndex = activeIndex !== null && activeIndex >= 0 && activeIndex < points.length ? activeIndex : null;
  const selected = selectedIndex === null ? null : points[selectedIndex];
  const barWidth = Math.min(32, ((plotWidth / Math.max(1, points.length)) * 0.82) / Math.max(1, series.length));
  const compactTick = (value: number) => value.toLocaleString(undefined, { notation: 'compact', maximumFractionDigits: 1 });
  const valueLabelIndices = new Set<number>();
  let lastLabelX = -Infinity;
  points.forEach((point, index) => {
    if (index !== 0 && index !== points.length - 1 && point.values[0] === points[index - 1].values[0]) return;
    if (index !== points.length - 1 && (x(index) - lastLabelX < 64 || (points.length > 1 && x(points.length - 1) - x(index) < 64))) return;
    valueLabelIndices.add(index);
    lastLabelX = x(index);
  });
  const inspect = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!points.length) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const position = ((event.clientX - bounds.left) / bounds.width) * width;
    let nearest = 0;
    for (let index = 1; index < points.length; index++) if (Math.abs(x(index) - position) < Math.abs(x(nearest) - position)) nearest = index;
    setActiveIndex(nearest);
  };
  return (
    <Box ref={hostRef} sx={{ width: 1, minWidth: 0, position: 'relative' }}>
      <Box component="svg" width="100%" height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${label}. Use left and right arrow keys to inspect values.`} tabIndex={0} onPointerMove={inspect} onPointerDown={inspect} onPointerLeave={() => setActiveIndex(null)} onFocus={() => setActiveIndex(points.length - 1)} onBlur={() => setActiveIndex(null)} onKeyDown={(event) => {
        if (!points.length || !['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'].includes(event.key)) return;
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex( event.key === 'Escape' ? null : event.key === 'Home' ? 0 : event.key === 'End' ? points.length - 1 : Math.max(0, Math.min(points.length - 1, (selectedIndex ?? points.length - 1) + (event.key === 'ArrowRight' ? 1 : -1))) );
      }} sx={{ display: 'block', overflow: 'visible', borderRadius: 1, '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 } }}>
        <title>{label}</title>
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={left} x2={width - right} y1={y(tick)} y2={y(tick)} stroke={theme.palette.divider} strokeDasharray={tick === 0 ? undefined : '3 4'} />
            <text x={left - 8} y={y(tick) + 4} textAnchor="end" fill={theme.palette.text.secondary} fontSize={11}>{compactTick(tick)}</text>
          </g>
        ))}
        {kind === 'line' &&
          series.map((item, seriesIndex) => {
            let connected = false;
            const path = points
              .map((point, index) => {
                const value = point.values[seriesIndex];
                if (value == null) {
                  connected = false;
                  return '';
                }
                const command = connected ? 'L' : 'M';
                connected = true;
                return `${command}${x(index)},${y(value)}`;
              })
              .join(' ');
            return (
              <g key={item.label}>
                <path d={path} fill="none" stroke={item.color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
                {points.map((point, index) => {
                  const value = point.values[seriesIndex];
                  return value == null || !valueLabelIndices.has(index) ? null : (
                    <g key={index}>
                      <circle cx={x(index)} cy={y(value)} r={3} fill={item.color} />
                      <text x={x(index)} y={y(value) - 9} textAnchor={index === 0 ? 'start' : index === points.length - 1 ? 'end' : 'middle'} fill={item.color} fontSize={12} fontWeight={600} stroke={theme.palette.background.paper} strokeWidth={3} paintOrder="stroke" >{formatChartNumber(value)}</text>
                    </g>
                  );
                })}
              </g>
            );
          })}
        {kind === 'bar' &&
          points.map((point, index) => (
            <g key={index}>
              {series.map((item, seriesIndex) => {
                const value = point.values[seriesIndex];
                if (value == null) return null;
                const barX = x(index) + (seriesIndex - series.length / 2) * barWidth + 1, barSize = Math.max(0.5, barWidth - 2), text = formatChartNumber(value);
                return (
                  <g key={item.label}>
                    <title>{`${point.detail}\n${item.label}: ${text}`}</title>
                    <rect x={barX} y={Math.min(y(value), y(0))} width={barSize} height={Math.max(value === 0 ? 0 : 1, Math.abs(y(value) - y(0)))} rx={2} fill={item.color} opacity={activeIndex === null || activeIndex === index ? 1 : 0.4} />
                    {barWidth >= text.length * 7 + 2 && (
                      <text x={barX + barSize / 2} y={Math.min(y(value), y(0)) - 7} textAnchor="middle" fill={item.color} fontSize={12} fontWeight={600}>{text}</text>
                    )}
                  </g>
                );
              })}
            </g>
          ))}
        {selected && selectedIndex !== null && (
          <g pointerEvents="none">
            <line x1={x(selectedIndex)} x2={x(selectedIndex)} y1={top} y2={height - bottom} stroke={theme.palette.text.secondary} strokeDasharray="3 4" opacity={0.5} />
            {kind === 'line' &&
              selected.values.map((value, index) => value === null ? null : ( <circle key={index} cx={x(selectedIndex)} cy={y(value)} r={4} fill={series[index].color} stroke={theme.palette.background.paper} strokeWidth={2} /> ) )}
          </g>
        )}
        {points.map((point, index) => labelIndices.has(index) ? (
          <text key={index} x={x(index)} y={height - 8} textAnchor={kind === 'line' && index === 0 ? 'start' : kind === 'line' && index === points.length - 1 ? 'end' : 'middle'} fill={theme.palette.text.secondary} fontSize={11} >{point.label}</text>
        ) : null
        )}
      </Box>
      {selected && selectedIndex !== null && (
        <Box role="tooltip" aria-live="polite" sx={{ position: 'absolute', zIndex: 2, top: 32, left: `${(Math.max(0, Math.min(width - 220, x(selectedIndex) + (x(selectedIndex) > width / 2 ? -224 : 12))) / width) * 100}%`, width: 208, maxWidth: '100%', boxSizing: 'border-box', p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper', boxShadow: 4, pointerEvents: 'none' }} >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{selected.detail}</Typography>
          {series.map((item, index) => (
            <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body2" sx={{ color: item.color }}>{item.label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatChartNumber(selected.values[index])}</Typography>
            </Box>
          ))}
          {selected.extra && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{selected.extra}</Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

const HistoryChartState = ({ loading, error, empty, children }: { loading: boolean; error: boolean; empty: boolean; children: React.ReactNode }) => loading ? (
  <Box sx={{ height: 190, display: 'grid', placeItems: 'center' }}>
    <CircularProgress size={20} />
  </Box>
) : error ? (
  <Box sx={{ height: 190, display: 'grid', placeItems: 'center' }}>
    <Typography color="text.secondary">History unavailable.</Typography>
  </Box>
) : empty ? (
  <Box sx={{ height: 190, display: 'grid', placeItems: 'center' }}>
    <Typography variant="body2" color="text.secondary">No history yet.</Typography>
  </Box>
) : (
  <>{children}</>
);
const HistoryMetric = ({ label, value, color, title }: { label: string; value: React.ReactNode; color?: string; title?: string }) => (
  <Tooltip title={title ?? ''}>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: color ?? 'text.secondary', display: 'block' }}>{label}</Typography>
      <Typography component="div" sx={{ fontSize: 22, lineHeight: 1.2, fontWeight: 600, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{value}</Typography>
    </Box>
  </Tooltip>
);

const ProductHistoryCharts = ({ productId }: { productId: string | number }) => {
  const theme = useTheme();
  const historyResponse = useProductPurchaseHistory(productId);
  const dispatchResponse = useGetProductDispatchRate(productId);
  const purchases = useMemo(() => getPurchaseHistoryPoints(historyResponse), [historyResponse]);
  const totals = useMemo(() => getPurchaseHistoryTotals(historyResponse), [historyResponse]);
  const dispatch = getDispatchData(dispatchResponse, productId);
  const stock = useMemo(() => getStockHistoryPoints(dispatch), [dispatch]);
  const historyState = historyResponse as any, dispatchState = dispatchResponse as any;
  const historyLoading = Boolean(historyState?.loading ?? historyState?.isLoading), dispatchLoading = Boolean(dispatchState?.loading ?? dispatchState?.isLoading);
  const historyError = Boolean(historyState?.error ?? historyState?.isError);
  // getDispatchOrderReferenceData returns order settings, NOT ProductDispatchRateData.
  // The warehouse hook must call the product dispatch-rate request; do not treat that mismatch as empty stock.
  const dispatchError = Boolean(dispatchState?.error ?? dispatchState?.isError) || isDispatchReferenceResponse(dispatchResponse);
  const cardSx = { minWidth: 0, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' };
  const latestStock = chartNumber(dispatch?.ItemsInStockEnd) ?? stock[stock.length - 1]?.values[0];
  const historyValue = (value: unknown) => (historyLoading || historyError ? '—' : formatChartNumber(value));
  const dispatchValue = (value: unknown) => (dispatchLoading || dispatchError ? '—' : formatChartNumber(value));
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', gap: 1.5, width: 1, minWidth: 0 }}>
      <Box sx={cardSx}>
        <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>Purchase history</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, md: 3 }, flexWrap: 'wrap', mb: 0.5 }}>
          <HistoryMetric label="Ordered" value={historyValue(totals.ordered)} color={theme.palette.primary.main} />
          <HistoryMetric label="Received" value={historyValue(totals.received)} color={theme.palette.success.main} />
          <HistoryMetric label="Order value" value={!historyLoading && !historyError && totals.money.length ? totals.money.map((value) => <Box key={value}>{value}</Box>) : '—'} title="Ordered quantity × unit price. Currencies are totaled separately; all rows must have a price." />
        </Box>
        <HistoryChartState loading={historyLoading} error={historyError} empty={!purchases.length}>
          <ProductHistoryChart points={purchases} series={[ { label: 'Ordered', color: theme.palette.primary.main }, { label: 'Received', color: theme.palette.success.main } ]} kind="bar" label="Purchase quantities by purchase order" />
        </HistoryChartState>
      </Box>
      <Box sx={cardSx}>
        <Typography component="h3" variant="subtitle2" sx={{ mb: 1 }}>Stock & dispatch</Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 2, md: 3 }, flexWrap: 'wrap', mb: 0.5 }}>
          <HistoryMetric label="In stock" value={dispatchValue(latestStock)} color={theme.palette.info.main} />
          <HistoryMetric label="Dispatched / week" value={dispatchValue(dispatch?.DispatchedItemsPerWeek)} />
          <HistoryMetric label="/ month" value={dispatchValue(dispatch?.DispatchedItemsPerMonth)} />
          <HistoryMetric label="/ 180 days" value={dispatchValue(dispatch?.DispatchedItemsPer180Days)} />
        </Box>
        <HistoryChartState loading={dispatchLoading} error={dispatchError} empty={!stock.length}>
          <ProductHistoryChart points={stock} series={[{ label: 'In stock', color: theme.palette.info.main }]} kind="line" label="Daily closing stock" />
        </HistoryChartState>
      </Box>
    </Box>
  );
};

